// =====================================================================
// MeloCommissionTab (Comissão Instituto Melo / Pablo — Fase B / S71) — a CENTRAL
// de comissão do Pablo, gated por company 3 no index. 5 seções:
//   Médico % (③, reusa finance_commission_rules) · Faixas (②) · Fixas (① R$110)
//   · Contratos (4ª etapa do gate) · Apuração (lista + resumo por pessoa + pagar + reconciliar).
// Backend isolado /finance/melo/* (+ /finance/commission-rules p/ o médico). Componente
// auto-contido (mantém o index enxuto), espelha o padrão dos outros *Tab.js.
// =====================================================================
import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Tabs, Tab, Tooltip, Typography, Checkbox, FormControlLabel
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import RefreshIcon from "@material-ui/icons/Refresh";
import PaymentIcon from "@material-ui/icons/Payment";
import { toast } from "react-toastify";
import {
  listCommissionRules, upsertCommissionRule, deleteCommissionRule,
  listMeloTiers, upsertMeloTier, deleteMeloTier,
  listMeloFixed, upsertMeloFixed, deleteMeloFixed,
  listMeloContracts, upsertMeloContract, deleteMeloContract,
  listMeloCommissions, meloCommissionsSummary, payMeloCommission, reconcileMeloCommissions,
  fmtMoney, financeErrorMessage
} from "../../services/financeService";
import useIsMobile from "../../utils/useIsMobile";

const useStyles = makeStyles((theme) => ({
  toolbar: { display: "flex", gap: theme.spacing(1.5), alignItems: "center", flexWrap: "wrap", margin: theme.spacing(1, 0, 2) },
  empty: { padding: theme.spacing(5), textAlign: "center", color: theme.palette.text.secondary },
  loading: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 140 },
  hint: { color: theme.palette.text.secondary, margin: theme.spacing(0.5, 0, 1.5), fontSize: 13 }
}));

const KIND = {
  medico: { label: "Médico", color: "#00838f" },
  tier: { label: "Venda (faixa)", color: "#1565c0" },
  fixed: { label: "Agendamento", color: "#ef6c00" }
};
const CSTATUS = {
  pendente: { label: "Pendente (gate)", color: "#b26a00" },
  apurada: { label: "A pagar", color: "#1565c0" },
  paga: { label: "Paga", color: "#2e7d32" },
  cancelada: { label: "Cancelada", color: "#757575" }
};
const num = (v) => (v == null || v === "" ? null : Number(v));
const fmtPct = (v) => (v == null ? "—" : `${Number(v)}%`);
const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "—";
};

const StatusChip = ({ map, value }) => {
  const c = map[value] || { label: value, color: "#757575" };
  return <Chip size="small" label={c.label} style={{ backgroundColor: c.color, color: "#fff" }} />;
};

const MeloCommissionTab = () => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const [sub, setSub] = useState(0);
  const [loading, setLoading] = useState(false);
  // dados
  const [rules, setRules] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [fixed, setFixed] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState([]);
  const [filters, setFilters] = useState({ from: "", to: "", kind: "", status: "" });
  // dialog genérico: { type, data }
  const [dlg, setDlg] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t, f, c] = await Promise.all([listCommissionRules(), listMeloTiers(), listMeloFixed(), listMeloContracts()]);
      setRules(r?.rules || []);
      setProfessionals(r?.professionals || []);
      setTiers(t?.tiers || []);
      setFixed(f?.fixed || []);
      setContracts(c?.contracts || []);
    } catch (e) { toast.error("Falha ao carregar a comissão."); }
    finally { setLoading(false); }
  }, []);

  const fetchApuracao = useCallback(async () => {
    setLoading(true);
    try {
      const p = {};
      if (filters.from) p.from = filters.from;
      if (filters.to) p.to = filters.to;
      if (filters.kind) p.kind = filters.kind;
      if (filters.status) p.status = filters.status;
      const [list, sum] = await Promise.all([listMeloCommissions(p), meloCommissionsSummary({ from: filters.from || undefined, to: filters.to || undefined })]);
      setCommissions(list?.commissions || []);
      setSummary(sum?.byPerson || []);
    } catch (e) { toast.error("Falha ao carregar a apuração."); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (sub === 4) fetchApuracao(); }, [sub, fetchApuracao]);

  const profName = (id) => { const p = professionals.find((x) => Number(x.id) === Number(id)); return p ? p.name : `#${id}`; };

  // ---- salvar (dialog genérico) ----
  const save = async () => {
    const { type, data } = dlg;
    setSaving(true);
    try {
      if (type === "rule") await upsertCommissionRule({ professionalId: num(data.professionalId), percent: num(data.percent), active: data.active !== false });
      else if (type === "tier") await upsertMeloTier({ id: data.id, scope: data.scope || "company", professionalId: num(data.professionalId), minAmount: num(data.minAmount) || 0, maxAmount: data.maxAmount === "" ? null : num(data.maxAmount), percent: num(data.percent), active: data.active !== false, notes: data.notes });
      else if (type === "fixed") await upsertMeloFixed({ id: data.id, trigger: data.trigger || "agendamento_confirmado", amount: num(data.amount) || 0, target: "scheduler_user", active: data.active !== false, notes: data.notes });
      else if (type === "contract") await upsertMeloContract({ id: data.id, patientId: num(data.patientId), quoteId: num(data.quoteId), status: data.status || "draft", value: data.value === "" ? null : num(data.value), notes: data.notes });
      toast.success("Salvo.");
      setDlg(null);
      fetchAll();
    } catch (e) { toast.error(financeErrorMessage(e)); }
    finally { setSaving(false); }
  };
  const del = async (type, id, label) => {
    if (!window.confirm(`Excluir ${label}?`)) return;
    try {
      if (type === "rule") await deleteCommissionRule(id);
      else if (type === "tier") await deleteMeloTier(id);
      else if (type === "fixed") await deleteMeloFixed(id);
      else if (type === "contract") await deleteMeloContract(id);
      fetchAll();
    } catch (e) { toast.error(financeErrorMessage(e)); }
  };
  const handlePay = async (c) => {
    if (!window.confirm(`Marcar como PAGA a comissão de ${fmtMoney(c.amount)}?`)) return;
    try { await payMeloCommission(c.id); toast.success("Comissão paga."); fetchApuracao(); }
    catch (e) { toast.error(financeErrorMessage(e)); }
  };
  const handleReconcile = async () => {
    try { const r = await reconcileMeloCommissions(); toast.success(`Reconciliado: ${r?.payments?.created || 0} nova(s), ${r?.gate?.released || 0} liberada(s) pelo gate.`); fetchApuracao(); }
    catch (e) { toast.error(financeErrorMessage(e)); }
  };

  const d = (patch) => setDlg((p) => ({ ...p, data: { ...p.data, ...patch } }));

  if (loading && sub !== 4) return <div className={classes.loading}><CircularProgress /></div>;

  return (
    <>
      <Tabs value={sub} onChange={(e, v) => setSub(v)} indicatorColor="primary" textColor="primary" variant="scrollable" style={{ marginBottom: 8 }}>
        <Tab label="Médico %" value={0} />
        <Tab label="Faixas (venda)" value={1} />
        <Tab label="Fixas (agendamento)" value={2} />
        <Tab label="Contratos" value={3} />
        <Tab label="Apuração" value={4} />
      </Tabs>

      {/* ---------- MÉDICO % (③) ---------- */}
      {sub === 0 && (
        <>
          <div className={classes.hint}>O repasse do médico — % sobre o recebido (ex.: 25% bruto). Cada profissional, uma regra.</div>
          <Box mb={1}><Button size="small" variant="contained" color="primary" startIcon={<AddIcon />}
            onClick={() => setDlg({ type: "rule", data: { active: true } })}>Nova regra de médico</Button></Box>
          <div style={{ overflowX: "auto", width: "100%" }}>
          <Table size="small">
            <TableHead><TableRow><TableCell>Profissional</TableCell><TableCell align="right">%</TableCell><TableCell align="center">Ativa</TableCell><TableCell align="center" style={{ width: 90 }}>Ações</TableCell></TableRow></TableHead>
            <TableBody>
              {rules.length === 0 ? <TableRow><TableCell colSpan={4} className={classes.empty}>Nenhuma regra de médico. Cadastre o % de cada um.</TableCell></TableRow>
                : rules.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.professionalName || profName(r.professionalId)}</TableCell>
                    <TableCell align="right">{fmtPct(r.percent)}</TableCell>
                    <TableCell align="center">{r.active ? "Sim" : "Não"}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => setDlg({ type: "rule", data: { professionalId: r.professionalId, percent: r.percent, active: r.active } })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Excluir"><IconButton size="small" onClick={() => del("rule", r.id, "a regra")}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          </div>
        </>
      )}

      {/* ---------- FAIXAS (②) ---------- */}
      {sub === 1 && (
        <>
          <div className={classes.hint}>Comissão da VENDA por faixa de valor (ex.: até R$7k = 2% · 7-10k = 3% · 10-15k = 4%). Faixa "Empresa" vale pra todos.</div>
          <Box mb={1}><Button size="small" variant="contained" color="primary" startIcon={<AddIcon />}
            onClick={() => setDlg({ type: "tier", data: { scope: "company", active: true } })}>Nova faixa</Button></Box>
          <div style={{ overflowX: "auto", width: "100%" }}>
          <Table size="small" style={{ minWidth: 640 }}>
            <TableHead><TableRow><TableCell>Alvo</TableCell><TableCell>Faixa</TableCell><TableCell align="right">%</TableCell><TableCell align="center">Ativa</TableCell><TableCell align="center" style={{ width: 90 }}>Ações</TableCell></TableRow></TableHead>
            <TableBody>
              {tiers.length === 0 ? <TableRow><TableCell colSpan={5} className={classes.empty}>Nenhuma faixa. Cadastre as faixas de % da venda.</TableCell></TableRow>
                : tiers.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell>{t.scope === "professional" ? profName(t.professionalId) : "Empresa (todos)"}</TableCell>
                    <TableCell>{fmtMoney(t.minAmount)} {t.maxAmount != null ? `– ${fmtMoney(t.maxAmount)}` : "ou +"}</TableCell>
                    <TableCell align="right">{fmtPct(t.percent)}</TableCell>
                    <TableCell align="center">{t.active ? "Sim" : "Não"}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => setDlg({ type: "tier", data: { ...t } })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Excluir"><IconButton size="small" onClick={() => del("tier", t.id, "a faixa")}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          </div>
        </>
      )}

      {/* ---------- FIXAS (①) ---------- */}
      {sub === 2 && (
        <>
          <div className={classes.hint}>Comissão de VALOR FIXO no agendamento (ex.: R$110) — pra quem agenda + confirma + recebe o sinal.</div>
          <Box mb={1}><Button size="small" variant="contained" color="primary" startIcon={<AddIcon />}
            onClick={() => setDlg({ type: "fixed", data: { trigger: "agendamento_confirmado", active: true } })}>Nova fixa</Button></Box>
          <div style={{ overflowX: "auto", width: "100%" }}>
          <Table size="small" style={{ minWidth: 640 }}>
            <TableHead><TableRow><TableCell>Gatilho</TableCell><TableCell align="right">Valor</TableCell><TableCell>Alvo</TableCell><TableCell align="center">Ativa</TableCell><TableCell align="center" style={{ width: 90 }}>Ações</TableCell></TableRow></TableHead>
            <TableBody>
              {fixed.length === 0 ? <TableRow><TableCell colSpan={5} className={classes.empty}>Nenhuma comissão fixa. Cadastre o valor do agendamento.</TableCell></TableRow>
                : fixed.map((f) => (
                  <TableRow key={f.id} hover>
                    <TableCell>{f.trigger === "agendamento_confirmado" ? "Agendamento confirmado" : f.trigger}</TableCell>
                    <TableCell align="right">{fmtMoney(f.amount)}</TableCell>
                    <TableCell>{f.target === "scheduler_user" ? "Quem agendou" : f.target}</TableCell>
                    <TableCell align="center">{f.active ? "Sim" : "Não"}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => setDlg({ type: "fixed", data: { ...f } })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Excluir"><IconButton size="small" onClick={() => del("fixed", f.id, "a fixa")}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          </div>
        </>
      )}

      {/* ---------- CONTRATOS ---------- */}
      {sub === 3 && (
        <>
          <div className={classes.hint}>4ª etapa do gate: o contrato assinado libera a comissão (cadastro + orçamento + consulta + CONTRATO).</div>
          <Box mb={1}><Button size="small" variant="contained" color="primary" startIcon={<AddIcon />}
            onClick={() => setDlg({ type: "contract", data: { status: "draft" } })}>Novo contrato</Button></Box>
          <div style={{ overflowX: "auto", width: "100%" }}>
          <Table size="small" style={{ minWidth: 640 }}>
            <TableHead><TableRow><TableCell>Paciente</TableCell><TableCell align="center">Status</TableCell><TableCell align="right">Valor</TableCell><TableCell align="center">Assinado</TableCell><TableCell align="center" style={{ width: 90 }}>Ações</TableCell></TableRow></TableHead>
            <TableBody>
              {contracts.length === 0 ? <TableRow><TableCell colSpan={5} className={classes.empty}>Nenhum contrato.</TableCell></TableRow>
                : contracts.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>{c.patientName || `#${c.patientId}`}</TableCell>
                    <TableCell align="center"><Chip size="small" label={c.status === "signed" ? "Assinado" : c.status === "cancelled" ? "Cancelado" : "Rascunho"} style={{ backgroundColor: c.status === "signed" ? "#2e7d32" : c.status === "cancelled" ? "#757575" : "#b26a00", color: "#fff" }} /></TableCell>
                    <TableCell align="right">{c.value != null ? fmtMoney(c.value) : "—"}</TableCell>
                    <TableCell align="center">{fmtDate(c.signedAt)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => setDlg({ type: "contract", data: { ...c } })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Excluir"><IconButton size="small" onClick={() => del("contract", c.id, "o contrato")}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          </div>
        </>
      )}

      {/* ---------- APURAÇÃO ---------- */}
      {sub === 4 && (
        <>
          <div className={classes.toolbar}>
            <TextField type="date" size="small" variant="outlined" label="De" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField type="date" size="small" variant="outlined" label="Até" value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField select size="small" variant="outlined" label="Tipo" value={filters.kind} onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value }))} style={{ flexGrow: 1, minWidth: 130 }}>
              <MenuItem value="">Todos</MenuItem><MenuItem value="medico">Médico</MenuItem><MenuItem value="tier">Venda (faixa)</MenuItem><MenuItem value="fixed">Agendamento</MenuItem>
            </TextField>
            <TextField select size="small" variant="outlined" label="Status" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} style={{ flexGrow: 1, minWidth: 130 }}>
              <MenuItem value="">Todos</MenuItem><MenuItem value="pendente">Pendente</MenuItem><MenuItem value="apurada">A pagar</MenuItem><MenuItem value="paga">Paga</MenuItem>
            </TextField>
            <Box flexGrow={1} />
            <Button size="small" startIcon={<RefreshIcon />} onClick={handleReconcile}>Reconciliar</Button>
          </div>

          {loading ? <div className={classes.loading}><CircularProgress /></div> : (
            <>
              <Typography variant="subtitle2" gutterBottom>Resumo por pessoa</Typography>
              {summary.length === 0 ? <div className={classes.empty}><Typography>Sem comissões no período. Clique em "Reconciliar" pra apurar as baixas.</Typography></div> : (
                <div style={{ overflowX: "auto", width: "100%" }}>
                <Table size="small" style={{ marginBottom: 16, minWidth: 640 }}>
                  <TableHead><TableRow><TableCell>Pessoa</TableCell><TableCell>Tipo</TableCell><TableCell align="right">A pagar</TableCell><TableCell align="right">Pendente</TableCell><TableCell align="right">Paga</TableCell></TableRow></TableHead>
                  <TableBody>
                    {summary.map((s, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{s.personName || "—"}</TableCell>
                        <TableCell><StatusChip map={KIND} value={s.kind} /></TableCell>
                        <TableCell align="right" style={{ color: "#1565c0", fontWeight: 600 }}>{fmtMoney(s.apurada)}</TableCell>
                        <TableCell align="right" style={{ color: "#b26a00" }}>{fmtMoney(s.pendente)}</TableCell>
                        <TableCell align="right" style={{ color: "#2e7d32" }}>{fmtMoney(s.paga)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}

              <Typography variant="subtitle2" gutterBottom>Comissões</Typography>
              {commissions.length === 0 ? <div className={classes.empty}><Typography>Nenhuma comissão.</Typography></div> : (
                <div style={{ overflowX: "auto", width: "100%" }}>
                <Table size="small" style={{ minWidth: 640 }}>
                  <TableHead><TableRow><TableCell>Tipo</TableCell><TableCell>Pessoa</TableCell><TableCell>Paciente</TableCell><TableCell align="right">Base</TableCell><TableCell align="right">%</TableCell><TableCell align="right">Comissão</TableCell><TableCell align="center">Status</TableCell><TableCell align="center" style={{ width: 70 }}>Pagar</TableCell></TableRow></TableHead>
                  <TableBody>
                    {commissions.map((c) => (
                      <TableRow key={c.id} hover>
                        <TableCell><StatusChip map={KIND} value={c.kind} /></TableCell>
                        <TableCell>{c.professionalName || c.userName || "—"}</TableCell>
                        <TableCell>{c.patientName || "—"}</TableCell>
                        <TableCell align="right">{fmtMoney(c.baseAmount)}</TableCell>
                        <TableCell align="right">{fmtPct(c.percent)}</TableCell>
                        <TableCell align="right" style={{ fontWeight: 600 }}>{fmtMoney(c.amount)}</TableCell>
                        <TableCell align="center"><StatusChip map={CSTATUS} value={c.status} /></TableCell>
                        <TableCell align="center">
                          {c.status === "apurada" ? <Tooltip title="Marcar como paga"><IconButton size="small" color="primary" onClick={() => handlePay(c)}><PaymentIcon fontSize="small" /></IconButton></Tooltip> : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ---------- DIALOG genérico (rule/tier/fixed/contract) ---------- */}
      <Dialog open={!!dlg} onClose={saving ? undefined : () => setDlg(null)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        {dlg && (
          <>
            <DialogTitle>
              {dlg.type === "rule" ? "Regra de médico (%)" : dlg.type === "tier" ? "Faixa de venda" : dlg.type === "fixed" ? "Comissão fixa" : "Contrato"}
            </DialogTitle>
            <DialogContent>
              {dlg.type === "rule" && (
                <>
                  <TextField select label="Profissional" value={dlg.data.professionalId || ""} onChange={(e) => d({ professionalId: e.target.value })} fullWidth margin="dense">
                    {professionals.map((p) => <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>)}
                  </TextField>
                  <TextField type="number" label="% do médico" value={dlg.data.percent ?? ""} onChange={(e) => d({ percent: e.target.value })} fullWidth margin="dense" />
                  <FormControlLabel control={<Checkbox checked={dlg.data.active !== false} onChange={(e) => d({ active: e.target.checked })} />} label="Ativa" />
                </>
              )}
              {dlg.type === "tier" && (
                <>
                  <TextField select label="Alvo" value={dlg.data.scope || "company"} onChange={(e) => d({ scope: e.target.value })} fullWidth margin="dense">
                    <MenuItem value="company">Empresa (todos)</MenuItem><MenuItem value="professional">Um profissional</MenuItem>
                  </TextField>
                  {dlg.data.scope === "professional" && (
                    <TextField select label="Profissional" value={dlg.data.professionalId || ""} onChange={(e) => d({ professionalId: e.target.value })} fullWidth margin="dense">
                      {professionals.map((p) => <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>)}
                    </TextField>
                  )}
                  <TextField type="number" label="Valor mínimo (R$)" value={dlg.data.minAmount ?? ""} onChange={(e) => d({ minAmount: e.target.value })} fullWidth margin="dense" />
                  <TextField type="number" label="Valor máximo (R$)" helperText="Deixe vazio = sem teto (use na última faixa)." value={dlg.data.maxAmount ?? ""} onChange={(e) => d({ maxAmount: e.target.value })} fullWidth margin="dense" />
                  <TextField type="number" label="% da faixa" value={dlg.data.percent ?? ""} onChange={(e) => d({ percent: e.target.value })} fullWidth margin="dense" />
                  <FormControlLabel control={<Checkbox checked={dlg.data.active !== false} onChange={(e) => d({ active: e.target.checked })} />} label="Ativa" />
                </>
              )}
              {dlg.type === "fixed" && (
                <>
                  <TextField label="Gatilho" value="Agendamento confirmado" fullWidth margin="dense" disabled helperText="Hoje só o agendamento confirmado." />
                  <TextField type="number" label="Valor (R$)" value={dlg.data.amount ?? ""} onChange={(e) => d({ amount: e.target.value })} fullWidth margin="dense" />
                  <TextField label="Alvo" value="Quem agendou" fullWidth margin="dense" disabled />
                  <FormControlLabel control={<Checkbox checked={dlg.data.active !== false} onChange={(e) => d({ active: e.target.checked })} />} label="Ativa" />
                </>
              )}
              {dlg.type === "contract" && (
                <>
                  <TextField type="number" label="ID do paciente (prontuário)" value={dlg.data.patientId ?? ""} onChange={(e) => d({ patientId: e.target.value })} fullWidth margin="dense" />
                  <TextField type="number" label="ID do orçamento (opcional)" value={dlg.data.quoteId ?? ""} onChange={(e) => d({ quoteId: e.target.value })} fullWidth margin="dense" />
                  <TextField select label="Status" value={dlg.data.status || "draft"} onChange={(e) => d({ status: e.target.value })} fullWidth margin="dense">
                    <MenuItem value="draft">Rascunho</MenuItem><MenuItem value="signed">Assinado</MenuItem><MenuItem value="cancelled">Cancelado</MenuItem>
                  </TextField>
                  <TextField type="number" label="Valor (R$, opcional)" value={dlg.data.value ?? ""} onChange={(e) => d({ value: e.target.value })} fullWidth margin="dense" />
                  <TextField label="Notas" value={dlg.data.notes ?? ""} onChange={(e) => d({ notes: e.target.value })} fullWidth margin="dense" multiline rowsMax={3} />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDlg(null)} disabled={saving}>Cancelar</Button>
              <Button variant="contained" color="primary" onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null}>Salvar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default MeloCommissionTab;
